/**
 * CPK-4758: shortcodes no enunciado (cp_question.statement) do questionário.
 *
 * Sintaxe: `{{ cp_pii.<caminho> }}` ou `{{ cp_pii.<caminho> | 'valor padrão' }}`.
 *  - o caminho desce em qualquer profundidade dos JSONB (ex.: primary_address.city);
 *  - sem valor, o placeholder some (string vazia) ou vira o valor padrão, se houver;
 *  - sem PII (pii nulo), todos os placeholders cp_pii somem — nunca aparece `{{ }}` cru;
 *  - placeholders que não começam com `cp_pii.` ficam intactos;
 *  - nunca lança: qualquer erro faz o placeholder sumir.
 *
 * Os valores do PII são escapados (HTML + markdown), pois o enunciado vai para
 * marked + bypassSecurityTrustHtml. O valor padrão é escrito pelo admin, como o
 * resto do enunciado, e entra sem escape.
 */

const SHORTCODE_RE = /\{\{\s*cp_pii\.([^{}]*?)\s*\}\}/g;

/** Aliases de campo, por objeto pai ('' = raiz do cp_pii). */
const ALIASES: Record<string, Record<string, string[]>> = {
  '': {
    first_name: ['attributes', 'first_name'],
    surname: ['attributes', 'surname'],
    last_name: ['attributes', 'last_name'],
    middle_name: ['attributes', 'middle_name'],
  },
  primary_address: {
    street: ['streetName'],
    number: ['streetNumber'],
    zip: ['postalCode'],
    cep: ['postalCode'],
    complement: ['unitNumber'],
  },
};

export function resolveStatementShortcodes(text: string | null | undefined, pii: any): string {
  if (!text) return '';
  return text.replace(SHORTCODE_RE, (_match, inner: string) => {
    try {
      const { path, fallback } = parseShortcode(inner);
      const value = pii ? formatValue(path, resolvePath(pii, path)) : '';
      if (value) return escapeValue(value);
      return fallback ?? '';
    } catch {
      return '';
    }
  });
}

function parseShortcode(inner: string): { path: string[]; fallback: string | null } {
  const pipe = inner.indexOf('|');
  const rawPath = (pipe >= 0 ? inner.slice(0, pipe) : inner).trim();
  let fallback: string | null = null;
  if (pipe >= 0) {
    const rawFallback = inner.slice(pipe + 1).trim();
    const quoted = rawFallback.match(/^(['"])([\s\S]*)\1$/);
    fallback = quoted ? quoted[2] : rawFallback;
  }
  const path = rawPath.split('.').map(p => p.trim()).filter(Boolean);
  return { path, fallback };
}

function resolvePath(pii: any, path: string[]): unknown {
  if (path.length === 0) return undefined;
  const [head, ...rest] = path;
  const rootAlias = ALIASES[''][head];
  if (rootAlias) return walk(pii, [...rootAlias, ...rest]);

  let current: any = pii?.[head];
  for (const key of rest) {
    if (current == null || typeof current !== 'object') return undefined;
    const alias = ALIASES[head]?.[key];
    current = key in current ? current[key] : alias ? walk(current, alias) : undefined;
  }
  return current;
}

function walk(obj: any, keys: string[]): unknown {
  return keys.reduce((acc, k) => (acc != null && typeof acc === 'object' ? acc[k] : undefined), obj);
}

function formatValue(path: string[], value: unknown): string {
  const [head] = path;
  const last = path[path.length - 1];

  if (head === 'primary_address' && path.length === 1) return formatAddress(value);
  if (value == null || typeof value === 'object') return '';

  const str = String(value).trim();
  if (!str) return '';
  if (head === 'date_of_birth') return formatDate(str);
  if (head === 'id_document_value') return formatCpf(str);
  if (head === 'primary_address' && ['postalCode', 'zip', 'cep'].includes(last)) return formatCep(str);
  if (head === 'contacts' && last === 'phone') return formatPhone(str);
  return str;
}

/** Endereço BR montado só com as partes existentes; formato UK cai no full_address. */
export function formatAddress(address: unknown): string {
  if (!address || typeof address !== 'object') return '';
  const a = address as Record<string, unknown>;
  const s = (k: string) => (a[k] == null || typeof a[k] === 'object' ? '' : String(a[k]).trim());
  const join = (sep: string, parts: string[]) => parts.filter(Boolean).join(sep);

  if (s('streetName') || s('city')) {
    const street = join(', ', [s('streetName'), s('streetNumber'), s('unitNumber')]);
    const head = join(' - ', [street, s('neighborhood')]);
    const cityState = join(' - ', [s('city'), s('state')]);
    return join(', ', [head, cityState, s('postalCode') ? formatCep(s('postalCode')) : '']);
  }
  return s('full_address') || join(', ', [s('address_line_1'), s('postcode')]);
}

function formatDate(str: string): string {
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : str;
}

function formatCpf(str: string): string {
  const d = str.replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : str;
}

function formatCep(str: string): string {
  const d = str.replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : str;
}

function formatPhone(str: string): string {
  const m = str.replace(/[^\d+]/g, '').match(/^\+?55(\d{2})(\d{4,5})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : str;
}

function escapeValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/([\\`*_[\]~|])/g, '\\$1');
}
